import { useCallback, useRef, useState } from 'react';
import type { BillingInfoData } from '@shipstation/financial-services-ui-components';

import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';

import type { PaymentMethodDetails } from '../../../financial-services-ui/types/fsui';
import { CarrierConnectionInteractionAction, CarrierConnectionInteractionSection, Workflows } from '../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { messages as rootMessages } from '../messages';

import { PaymentMethodSection } from './payment-method-section';
import { WwexSubViewFooter } from './wwex-sub-view-footer';
import { WwexSubViewHeader } from './wwex-sub-view-header';
import modalStyle from '../wwex-registration-modal.module.less';

type AddPaymentMethodViewProps = {
  headerId: string;
  isDisabled: boolean;
  onPaymentMethodCreated: (paymentMethodId: string, billingInfo?: BillingInfoData, paymentMethod?: PaymentMethodDetails) => void;
  onBack: () => void;
  onCancel: () => void;
};

export const AddPaymentMethodView = ({ headerId, isDisabled, onPaymentMethodCreated, onBack, onCancel }: AddPaymentMethodViewProps) => {
  const submitPaymentRef = useRef<(() => void) | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const isViewDisabled = isDisabled || isProcessingPayment;
  const trackCarrierConnectionStep = useTrackWorkflow(Workflows.CARRIER_CONNECTION_FLOW);

  const handleSubmitFunctionReady = useCallback((fn: (() => void) | null) => {
    submitPaymentRef.current = fn;
  }, []);

  const handleSave = useCallback(() => {
    submitPaymentRef.current?.();
  }, []);

  const handleCancel = useCallback(() => {
    trackCarrierConnectionStep(WorkflowSteps.INTERACTION, {
      section: CarrierConnectionInteractionSection.PAYMENT,
      action: CarrierConnectionInteractionAction.CANCELLED,
    });
    onCancel();
  }, [onCancel, trackCarrierConnectionStep]);

  const handleBack = useCallback(() => {
    trackCarrierConnectionStep(WorkflowSteps.INTERACTION, {
      section: CarrierConnectionInteractionSection.PAYMENT,
      action: CarrierConnectionInteractionAction.WENT_BACK,
    });
    onBack();
  }, [onBack, trackCarrierConnectionStep]);

  return (
    <>
      <ModalHeader className={modalStyle.modalHeader} closeModal={handleCancel} id={headerId}>
        <WwexSubViewHeader isDisabled={isViewDisabled} onBack={handleBack} titleMessage={rootMessages.addPaymentMethodTitle} />
      </ModalHeader>

      <ModalBody className={modalStyle.modalBody}>
        <PaymentMethodSection
          onPaymentMethodCreated={onPaymentMethodCreated}
          onProcessingChange={setIsProcessingPayment}
          onSubmitFunctionReady={handleSubmitFunctionReady}
        />
      </ModalBody>

      <ModalFooter>
        <WwexSubViewFooter isDisabled={isViewDisabled} onBack={handleBack} onCancel={handleCancel} onSave={handleSave} />
      </ModalFooter>
    </>
  );
};
